import * as React from "react";
import { Link, Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";

export interface MarketingNavItem {
  label: string;
  to: string;
}

export interface MarketingLayoutProps {
  /** Top nav items. Defaults to Product/Pricing/Customers/Blog/Docs. */
  navItems?: MarketingNavItem[];
  /** Brand mark slot (defaults to text wordmark). */
  brand?: React.ReactNode;
  /** Right-side CTA slot (login/signup buttons). */
  cta?: React.ReactNode;
  /** Footer slot (defaults to a minimal footer). */
  footer?: React.ReactNode;
  children?: React.ReactNode;
}

const DEFAULT_NAV: MarketingNavItem[] = [
  // Removed "Product" — it routed to "/" (same as logo), a dead-end/redundant link.
  // Reintroduce when a dedicated product/features page exists.
  { label: "Pricing", to: "/pricing" },
  { label: "Customers", to: "/case-studies" },
  { label: "Blog", to: "/blog" },
  { label: "Docs", to: "/docs" },
];

/**
 * Marketing/public site shell — translucent sticky topbar, generous gutters,
 * minimal footer. Big-type marketing uses the redesign type scale.
 */
export function MarketingLayout({
  navItems = DEFAULT_NAV,
  brand,
  cta,
  footer,
  children,
}: MarketingLayoutProps) {
  return (
    <div className="flex min-h-dvh w-full flex-col bg-[hsl(var(--rd-canvas))] text-[hsl(var(--rd-text-1))]">
      {/* Skip link — visible only on keyboard focus */}
      <a
        href="#rd-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-[hsl(var(--rd-accent-500))] focus:px-3 focus:py-2 focus:text-[13px] focus:font-medium focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--rd-accent-300))]"
      >
        Skip to main content
      </a>
      <MarketingTopBar navItems={navItems} brand={brand} cta={cta} />
      <main id="rd-main" tabIndex={-1} className="flex-1 focus:outline-none">
        {children ?? <Outlet />}
      </main>
      {footer ?? <DefaultFooter />}
    </div>
  );
}

function MarketingTopBar({
  navItems,
  brand,
  cta,
}: {
  navItems: MarketingNavItem[];
  brand?: React.ReactNode;
  cta?: React.ReactNode;
}) {
  return (
    <header
      className={cn(
        "sticky top-0 z-30 flex h-14 items-center border-b border-[hsl(var(--rd-border))]/60 bg-[hsl(var(--rd-canvas))]/80 backdrop-blur-md"
      )}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center gap-8 px-6">
        <Link
          to="/"
          className="flex items-center gap-2 text-[14.5px] font-semibold tracking-tight text-[hsl(var(--rd-text-1))]"
        >
          {brand ?? <BrandWordmark />}
        </Link>

        <nav className="hidden flex-1 items-center gap-1 md:flex">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="rounded-md px-3 py-1.5 text-[13.5px] font-medium text-[hsl(var(--rd-text-2))] transition-colors hover:bg-[hsl(var(--rd-surface-2))] hover:text-[hsl(var(--rd-text-1))]"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">{cta}</div>
      </div>
    </header>
  );
}

/** Minimal SVG wordmark — abstract, monochrome, currentColor. */
function BrandWordmark() {
  return (
    <span className="flex items-center gap-2">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-label="CreatorVault">
        <path
          d="M3 5h11l3 5-3 5H3l3-5-3-5z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
      <span>CreatorVault</span>
    </span>
  );
}

function DefaultFooter() {
  return (
    <footer className="border-t border-[hsl(var(--rd-border))] py-10">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 text-[12.5px] text-[hsl(var(--rd-text-3))] md:flex-row md:items-center md:justify-between">
        <div>© {new Date().getFullYear()} CreatorVault. All rights reserved.</div>
        <div className="flex flex-wrap gap-4">
          <Link to="/privacy-policy" className="hover:text-[hsl(var(--rd-text-2))]">
            Privacy
          </Link>
          <Link to="/terms-of-service" className="hover:text-[hsl(var(--rd-text-2))]">
            Terms
          </Link>
          <Link to="/contact" className="hover:text-[hsl(var(--rd-text-2))]">
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}
